import { MOBILE_CHAT_LAUNCHER_PATHNAMES } from './mobile-chat-launcher-routes';

/**
 * The pages that get the sticky "Ask {maestro}" bar ON A PHONE — the web twin of the phones'
 * `stickyChatPill` (#11688 + #11928). Every other narrow route has none: an unknown route defaults OUT.
 *
 * Two groups:
 *  - the routes the phones floated the round chat bubble on (`MOBILE_CHAT_LAUNCHER_PATHNAMES`);
 *  - the content pages the phones wrap with `StickyChatPage` (iOS `Route.getsStickyChat`, Android's
 *    `StickyChatPage` wrappers in `AppRoot`). Matched through the BI page name both apps already share
 *    for each screen (`lib/bi-page-name.ts`), noted beside each entry.
 *
 * Left out on purpose, as on the phones: chat itself and screens with their own composer or search
 * field (`/chats`, a contact's thread), and settings, forms, flows (connect, packages, checkout,
 * pack accept, profile links) and dev tools.
 */
export const STICKY_CHAT_NARROW_PATHNAMES: readonly string[] = [
  ...MOBILE_CHAT_LAUNCHER_PATHNAMES,
  '/agents/[id]', // agent_detail
  '/agents/activity', // activity_log
  '/activity', // agent_activity_log
  '/tasks', // agent_tasks
  '/tasks/[id]', // task_screen
  '/chase/[itemId]', // chase_item
  '/goals', // agent_goals
  '/workspace/[slug]/goals', // redirect onto /goals
  '/routines', // routines
  '/pulses', // pulses
  '/contacts', // contacts
  '/contacts/[id]', // contact_detail
  '/wins', // wins
  '/knowledge', // knowledge
  '/files', // files
  '/workspace/[slug]/files', // agent_creations
  '/workspace/[slug]/files/[file]', // file_detail
  '/calendar', // calendar
  '/workspace/[slug]/calendar', // calendar
  '/apps', // apps
  '/workspace/[slug]/apps', // workspace_apps
  '/packs', // packs
  '/packs/[slug]', // pack_detail
  '/notifications', // notifications
  '/dashboard/[slug]', // dashboard_page
  '/pages/[slug]', // dashboard_page
  '/workspace/[slug]/pages/[page]', // dashboard_page
];

/**
 * Whether a phone shows the sticky bar on [pathname] (the Next `router.pathname` pattern). Wide is not
 * asked: there the bar shows on every route, because on a desktop it sits below the content and is the
 * only always-available door into the chat dock.
 */
export function showsStickyChatOnNarrow(pathname: string): boolean {
  const normalized = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return STICKY_CHAT_NARROW_PATHNAMES.includes(normalized);
}

/** The bar's resting height and its gap to the bottom edge (native `restingPillHeight`, `fabEdgeInset`). */
export const STICKY_CHAT_HEIGHT_PX = 50;
export const STICKY_CHAT_BOTTOM_PX = 20;
/**
 * What a page reserves at the end of its scroll while the bar shows, so the last row can scroll clear of
 * it (native `stickyPageInset`: the pill, its bottom inset, and 16 of air).
 */
export const STICKY_CHAT_PAGE_INSET_PX = STICKY_CHAT_HEIGHT_PX + STICKY_CHAT_BOTTOM_PX + 16;
